import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ArrowRightIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import type { PracticeListItem } from '../../../../services/accounts';
import { movePatientPractice } from '../../../../services/patientEdit';
import { useToast } from '../../../../providers/ToastProvider';
import { DmShellLayout } from '../dm/DmShellLayout';
import { SkeletonCard } from '../dm/DmSkeletons';
import { DmBtnPrimary, DmBtnSecondary } from '../dm/DmFormPrimitives';
import type { ActionRunnerProps } from '../types';
import { PracticePickerList } from './PracticePickerList';

interface ConfirmMoveStepProps {
    patientName: string;
    currentPracticeName: string;
    targetPractice: PracticeListItem;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

function ConfirmMoveStep({
    patientName,
    currentPracticeName,
    targetPractice,
    isPending,
    onConfirm,
    onCancel
}: ConfirmMoveStepProps) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col gap-6">
            <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    You are about to move
                </div>
                <div className="text-base font-semibold text-gray-900">{patientName}</div>
            </div>
            <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
                    <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{currentPracticeName}</span>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-800 bg-white">
                    <BuildingOffice2Icon className="h-4 w-4 text-gray-800 shrink-0" />
                    <span className="text-sm font-semibold text-gray-900 truncate">
                        {targetPractice.name}
                    </span>
                </div>
            </div>
            <div className="flex justify-end gap-2">
                <DmBtnSecondary onClick={onCancel} disabled={isPending}>
                    Back
                </DmBtnSecondary>
                <DmBtnPrimary onClick={onConfirm} disabled={isPending}>
                    {isPending ? 'Moving…' : 'Confirm move'}
                </DmBtnPrimary>
            </div>
        </div>
    );
}

function useMovePracticeMutation(
    patientId: string,
    refetch: () => Promise<void>,
    onBack: () => void,
    onDataChange?: () => void
) {
    const { showToast } = useToast();

    const mutation = useMutation({
        mutationFn: (practiceId: string) => movePatientPractice(patientId, practiceId),
        onSuccess: async () => {
            await refetch();
            onDataChange?.();
            showToast('Patient moved to the new practice.', 'success');
            onBack();
        },
        onError: (error) => {
            showToast(error instanceof Error ? error.message : 'Failed to move patient.', 'error');
        }
    });

    return mutation;
}

function MovePracticeLanding({ patientId, patientName, onBack, onDataChange }: ActionRunnerProps) {
    const { data: card, isLoading: cardLoading, refetch } = usePatientCard(patientId);
    const [targetPractice, setTargetPractice] = useState<PracticeListItem | null>(null);

    const mutation = useMovePracticeMutation(patientId, refetch, onBack, onDataChange);

    const currentPracticeId = card?.practice?.id ?? null;
    const currentPracticeName = card?.practice?.name ?? 'No practice assigned';

    const handleConfirm = () => {
        if (!targetPractice) return;
        mutation.mutate(targetPractice.id);
    };

    return (
        <DmShellLayout title="Move to another practice" onBack={onBack}>
            <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
                {cardLoading && <SkeletonCard />}

                {!cardLoading && targetPractice && (
                    <ConfirmMoveStep
                        patientName={patientName}
                        currentPracticeName={currentPracticeName}
                        targetPractice={targetPractice}
                        isPending={mutation.isPending}
                        onConfirm={handleConfirm}
                        onCancel={() => setTargetPractice(null)}
                    />
                )}

                {!cardLoading && !targetPractice && (
                    <>
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Current practice
                            </div>
                            <div className="flex items-center gap-2">
                                <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                                <span className="text-base font-semibold text-gray-900">
                                    {currentPracticeName}
                                </span>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col gap-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Move to
                            </div>
                            <PracticePickerList
                                currentPracticeId={currentPracticeId}
                                onSelect={setTargetPractice}
                            />
                        </div>
                    </>
                )}
            </div>
        </DmShellLayout>
    );
}

export { MovePracticeLanding };
